import type { Effect } from "../engine/effects/effect";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "../engine/effects/movementEffect";
import type { AnimationLoader } from "./animationLoader";
import type { Sprite } from "./sprites/sprite";
import { ManagedAnimation } from "./sprites/managedAnimation";

export const STD_UI_MOVEMENT_TIME = 140;

export function scaleEngineTimeToUi(time: number): number {
	return (time / STANDARD_MOVEMENT_TIME) * STD_UI_MOVEMENT_TIME;
}

export class EffectManager {
	private managedEffects: Map<Effect, ManagedAnimation> = new Map();
	private expirableEffectsCount: number = 0;
	private expiredEffectsCount: number = 0;
	private startingAnimationOrder: number | null = null;
	private currentAnimationOrder: number | null = null;
	private animationLoader: AnimationLoader;

	constructor(animationLoader: AnimationLoader) {
		this.animationLoader = animationLoader;
	}

	async addEffect(effect: Effect): Promise<void> {
		const animation = await this.animationLoader.getAnimationForEffect(effect);
		if (animation === null) {
			this.expirableEffectsCount++;
			this.expiredEffectsCount++;
			this.updateStartingOrder(effect.getAnimationOrder());
			return;
		}

		const managedAnim = new ManagedAnimation(animation);

		if (effect instanceof MovementEffect) {
			managedAnim.setMaxTime(scaleEngineTimeToUi(effect.time));
		}

		this.managedEffects.set(effect, managedAnim);

		if (!animation.loop || effect instanceof MovementEffect) {
			this.expirableEffectsCount++;
		}

		this.updateStartingOrder(effect.getAnimationOrder());
	}

	async addEffects(effects: Effect[]): Promise<void> {
		for (const effect of effects) {
			await this.addEffect(effect);
		}
	}

	clearEffects(): void {
		this.managedEffects.clear();
		this.expirableEffectsCount = 0;
		this.expiredEffectsCount = 0;
		this.startingAnimationOrder = null;
		this.currentAnimationOrder = null;
	}

	passTime(ms: number): void {
		if (this.managedEffects.size === 0) return;

		if (this.currentAnimationOrder === null) {
			this.currentAnimationOrder = this.startingAnimationOrder;
		}

		for (const [effect, managedAnim] of this.managedEffects) {
			if (effect.getAnimationOrder() !== this.currentAnimationOrder) continue;

			if (!managedAnim.isExpired()) {
				managedAnim.passTime(ms);

				if (managedAnim.isExpired()) {
					this.expiredEffectsCount++;
				}
			}
		}

		if (this.allAtCurrentOrderExpired()) {
			this.advanceAnimationOrder();
		}
	}

	getEffectSprite(effect: Effect): Sprite | null {
		const managedAnim = this.managedEffects.get(effect);
		if (!managedAnim) return null;
		if (managedAnim.isExpired() && !(effect instanceof MovementEffect)) return null;
		return managedAnim.getCurrentSprite();
	}

	isAllExpired(): boolean {
		return this.expirableEffectsCount <= this.expiredEffectsCount;
	}

	getElapsedTime(effect: Effect): number {
		const managedAnim = this.managedEffects.get(effect);
		if (!managedAnim) return 0;
		return managedAnim.getTotalElapsedTime();
	}

	getAllEffects(): IterableIterator<Effect> {
		return this.managedEffects.keys();
	}

	isEffectExpired(effect: Effect): boolean {
		const managedAnim = this.managedEffects.get(effect);
		if (!managedAnim) return true;
		return managedAnim.isExpired();
	}

	hasStarted(): boolean {
		return this.currentAnimationOrder !== null;
	}

	getCurrentAnimationOrder(): number | null {
		return this.currentAnimationOrder;
	}

	private updateStartingOrder(order: number): void {
		if (this.startingAnimationOrder === null || order < this.startingAnimationOrder) {
			this.startingAnimationOrder = order;
		}
	}

	private allAtCurrentOrderExpired(): boolean {
		for (const [effect, managedAnim] of this.managedEffects) {
			if (effect.getAnimationOrder() !== this.currentAnimationOrder) continue;
			if (!managedAnim.isExpired()) return false;
		}
		return true;
	}

	private advanceAnimationOrder(): void {
		let nextOrder: number | null = null;
		for (const [effect] of this.managedEffects) {
			const order = effect.getAnimationOrder();
			if (order > this.currentAnimationOrder!) {
				if (nextOrder === null || order < nextOrder) {
					nextOrder = order;
				}
			}
		}

		if (nextOrder !== null) {
			this.currentAnimationOrder = nextOrder;
		}
	}
}
